import { store } from 'store';

export const ActionsNames = {
  viewcrud: 'viewcrud',
  view: 'view',
  add: 'add',
  change: 'change',
  delete: 'delete',
  authorize: 'autorizar',
  cancel: 'delete',
  export: 'exportar',
  import: 'importar',
  sync: 'sincronizar',
};

// Rutas accesibles para todos los usuarios
const alwaysAllowedRoutes = [
  '/dashboard',
  // '/configuracion/entidad',
  // '/configuracion/cuenta-de-usuario',
];

// Rutas que aun no se encuentran disponibles
const currentExceptions = [
  '/reporte',
  // '/configuracion/zonas-horarias',
  // '/configuracion/variables-o-conceptos-globales',
  // '/contabilidad/clave-contable',
  // '/contabilidad/clave-presupuestal',
  // '/configuracion/cfdi-y-factura', // recibo
];

const allExceptions = [...alwaysAllowedRoutes, ...currentExceptions];

/**
  * Función para identificar si el usuario es un administrador de la entidad
  * @returns {boolean}
 */
export const isAdmin = () => {
  const { user, entidad } = store.getState()?.auth;
  return user?.es_administrador || entidad?.administradores.includes(user.id);
};

export const isInspector = () => {
  const { roles, entidad } = store.getState()?.auth;
  return roles?.some((r) => r.id === entidad?.rol_inspector);
};

/**
  * Función para identificar si el usuario es staff
  * @returns {boolean}
 */
export const isStaff = () => store.getState()?.auth?.user?.is_staff;

/**
  * Función para identificar si el usuario tiene el permiso para una acción específica
  * @param {string} model - Modelo
  * @param {string} type - Tipo de accion
  * @returns {boolean}
 */
export const hasPermission = (permission = '') => store?.getState()?.auth?.user?.permisos
  .includes(permission.toLowerCase()) || isStaff();

/**
  * Función para identificar si el usuario tiene el permiso para una acción específica
  * @param {string} model - Modelo
  * @param {string} type - Tipo de accion
  * @returns {boolean}
 */
export const hasActionPermission = (model, type) => hasPermission(`${type}_${model}`);

/**
  * Función para obtener los permisos de un usuario para un modelo específico
  * @param {string} model - Modelo
  * @param {string[]} [catalogsModels] - Catálogos del Modelo
  * @param {Object[]} [tabsModels] - Modelos de los tabs de la ruta
  * @param {string} tabsModels[].model - Nombre del modelos
  * @param {string[]} tabsModels[].catalogs - Catálogos del Modelo
  * @returns {Object} permission
 */
export const getModelPermission = (
  model,
  catalogsModels = [],
  tabs = [],
  isTab = false,
) => {
  const keys = Object.keys(ActionsNames);
  const entries = keys.map((key) => [key, hasActionPermission(model, ActionsNames[key])]);
  const permission = Object.fromEntries(entries);
  permission.model = model;
  const catalogsPermission = catalogsModels.map((m) => getModelPermission(m));
  if (catalogsModels.length) {
    permission.catalogs = Object.fromEntries(
      catalogsModels.map((e, idx) => [e, catalogsPermission[idx]]),
    );
  }
  if (tabs.length) {
    const tabsPermissions = tabs.map((m) => getModelPermission(m.model, m.catalogs, [], true));
    permission.tabs = Object.fromEntries(tabs.map((e, i) => [e.model, tabsPermissions[i]]));
  }
  permission.hasAllViewPermission = permission[isTab ? 'view' : 'viewcrud'] && catalogsPermission.every((e) => e.view);
  return permission;
};

/**
  * Función para obtener los permisos de un usuario para una configuracion especifica
  * @param {permissionConfig} permissionConfig - configuracion de permisos
  * @param {string} permissionConfig.permissionModel - Nombre del modelo base de la ruta
  * @param {string} permissionConfig.mainPermission - Nombre del permiso base de la ruta
  * @param {string[]} permissionConfig.required - Nombre de los permisos de las
  *   dependencias que tiene la ruta
  * @param {string[]} permissionConfig.catalogsModels - Catálogos del Modelo
  * @param {Object[]} permissionConfig.tabsModels - Modelos de los tabs de la ruta
  * @param {string} permissionConfig.tabsModels[].model - Nombre del modelos
  * @param {string[]} permissionConfig.tabsModels[].catalogs - Catálogos del Modelo
  * @returns {object} permission
 */
export const getPermission = ({
  permissionModel,
  catalogsModels = [],
  tabsModels = [],
  mainPermission,
  required = [],
  customActions = [],
  customSections = [],
}) => {
  let permission = {};
  if (!mainPermission && !permissionModel) {
    return {};
  }
  customActions.forEach((action) => {
    permission[action.name] = action.permissions.every((p) => hasPermission(p));
  });
  customSections.forEach((section) => {
    section.models?.forEach((model) => {
      const _permission = `${section.action}_${model}`;
      permission[_permission] = hasPermission(_permission);
    });
    section.permissions?.forEach((_permission) => {
      permission[_permission] = hasPermission(_permission);
    });
    permission[section.name] = section.models
      ? section.models.some((model) => permission[`${section.action}_${model}`])
      : section.permissions.some((_permission) => permission[_permission]);
  });
  required.forEach((_permission) => {
    permission[_permission] = hasPermission(_permission);
  });
  if (permissionModel && !mainPermission) {
    permission = {
      ...permission,
      ...getModelPermission(
        permissionModel,
        catalogsModels,
        tabsModels,
      ),
    };
    // TODO: Eliminar ya que existan permisos
    permission.import = true;
    permission.export = true;
    permission.cancel = permission.authorize;

    return permission;
  }

  if (mainPermission && !permissionModel) {
    permission[mainPermission] = hasPermission(mainPermission);
    permission.hasAllViewPermission = permission[mainPermission]
      && required.every((p) => permission[p]);

    return permission;
  }
  return {};
};

/**
  * Función para identificar si el usuario tiene el permiso de vista para una ruta específica
  * @param {string} path - Path de la ruta
  * @param {permissionConfig} permissionConfig - configuracion de permisos
  * @param {string} permissionConfig.permissionModel - Nombre del modelo base de la ruta
  * @param {string} permissionConfig.mainPermission - Nombre del permiso base de la ruta
  * @param {string[]} permissionConfig.required - Nombre de los permisos de las
  *   dependencias que tiene la ruta
  * @returns {boolean}
 */
export const routePermission = (path, permissionConfig) => {
  const { permissionModel, mainPermission: mainP, required = [] } = permissionConfig;
  if (isStaff() || allExceptions.includes(path) || (!permissionModel && !mainP)) return true;
  if (mainP) return hasPermission(mainP) && required.every((p) => hasPermission(p));
  if (permissionModel) return hasActionPermission(permissionModel, ActionsNames.viewcrud);
  return false;
};
